import React from 'react';

const PDParentStreak = () => {
  return (
    <div className="space-y-4">
        <div className="bg-gradient-to-r from-cyan-900/30 to-purple-900/30 border border-cyan-800 rounded-xl p-4">
            <h2 className="text-sm lg:text-lg text-cyan-200 font-semibold mb-2">
                👨‍👩‍👧‍👦 PurpleDoor Parent & Mentor Streaks
            </h2>

            <p className="text-gray-100 text-xs lg:text-sm">
                PurpleDoor Parent & Mentor Streaks empowers parents, teachers, mentors, coaches, trainers and professionals to encourage consistent positive behaviours through structured streak journeys.
            </p>

            <p className="text-gray-100 text-xs lg:text-sm mt-2">
                Whether it is studying, reading, fitness, therapy compliance, skill development, habit formation, personal growth or professional learning, streaks help transform good intentions into consistent action.
            </p>

            <p className="text-gray-100 text-xs lg:text-sm mt-2">
                The platform enables mentors and institutions to create meaningful engagement journeys while recognising and rewarding sustained effort rather than one-time achievements.
            </p>
        </div>

        {/* WHO CAN USE */}
        <div className="bg-gray-800/70 border border-gray-700 rounded-xl p-4">
            <h2 className="text-sm lg:text-lg text-cyan-200 font-semibold mb-3">
                🎯 Who Can Create Streak Programs?
            </h2>

            <ul className="list-disc pl-5 space-y-2">
                <li className="text-gray-100 text-xs lg:text-sm">
                    Parents and Family Members
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    School Teachers and Educators
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Tuition Teachers and Academic Mentors
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Corporate Trainers and Learning Teams
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Doctors and Healthcare Professionals
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Therapists and Counsellors
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Life Coaches, Fitness Coaches and Skill Coaches
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Educational Institutions, NGOs and Government Programs
                </li>
            </ul>
        </div>

        {/* STREAK FORMATS */}
        <div className="bg-gray-800/70 border border-gray-700 rounded-xl p-4">
            <h2 className="text-sm lg:text-lg text-cyan-200 font-semibold mb-3">
                📅 Flexible Streak Durations
            </h2>

            <p className="text-gray-100 text-xs lg:text-sm">
                Every mentor can choose the streak duration that best aligns with their objectives and participant journey.
            </p>

            <ul className="list-disc pl-5 mt-3 space-y-2">
                <li className="text-gray-100 text-xs lg:text-sm">
                    <strong>7-Day Streaks</strong> — Quick habit formation and short-term goals.
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    <strong>30-Day Streaks</strong> — Consistency building and behavioural reinforcement.
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    <strong>90-Day Streaks</strong> — Long-term transformation and measurable outcomes.
                </li>
            </ul>
        </div>

        {/* HOW IT WORKS */}
        <div className="bg-gray-800/70 border border-gray-700 rounded-xl p-4">
            <h2 className="text-sm lg:text-lg text-cyan-200 font-semibold mb-3">
                ⚡ How Parent & Mentor Streaks Work
            </h2>

            <ol className="list-decimal pl-5 space-y-2">
                <li className="text-gray-100 text-xs lg:text-sm">
                    Mentor defines the objective, activity and streak duration.
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Participants complete assigned tasks or behavioural milestones.
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Mentor reviews and approves submissions or achievements.
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Approved activities are converted into validated streak progress.
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Participants maintain streaks through consistency and accountability.
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Rewards are unlocked based on mentor-defined criteria.
                </li>
            </ol>
        </div>

        {/* APPROVAL SYSTEM */}
        <div className="bg-gradient-to-r from-purple-900/30 to-cyan-900/30 border border-purple-700 rounded-xl p-4">
            <h2 className="text-sm lg:text-lg text-cyan-200 font-semibold mb-3">
                ✅ Full Approval Control
            </h2>

            <p className="text-gray-100 text-xs lg:text-sm">
                Every Parent, Mentor, Teacher, Coach, Therapist or Institution retains complete control over their streak program.
            </p>

            <ul className="list-disc pl-5 mt-3 space-y-2">
                <li className="text-gray-100 text-xs lg:text-sm">
                    Define custom streak objectives.
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Set approval criteria.
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Decide validation methods.
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Create personalised reward structures.
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Reward individuals, teams or entire groups.
                </li>
            </ul>
        </div>

        {/* REWARD OPTIONS */}
        <div className="bg-gray-800/70 border border-gray-700 rounded-xl p-4">
            <h2 className="text-sm lg:text-lg text-cyan-200 font-semibold mb-3">
                🎁 Custom Reward Programs
            </h2>

            <p className="text-gray-100 text-xs lg:text-sm">
                Mentors can create rewards that best motivate their participants and align with their goals.
            </p>

            <ul className="list-disc pl-5 mt-3 space-y-2">
                <li className="text-gray-100 text-xs lg:text-sm">
                    Certificates and Recognition
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Fee Discounts or Scholarships
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Exclusive Sessions or Mentoring Access
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Learning Resources and Course Unlocks
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Community Recognition and Leaderboards
                </li>
            </ul>
        </div>

        {/* COLLECTIBLES */}
        <div className="bg-gradient-to-r from-cyan-900/30 to-purple-900/30 border border-cyan-800 rounded-xl p-4">
            <h2 className="text-sm lg:text-lg text-cyan-200 font-semibold mb-3">
                🏆 Physical Collectibles (Think5Whys DOORI - Discipline Opens Outstanding Results & Impact) for 12-Week Continuous Streaks
            </h2>

            <p className="text-gray-100 text-xs lg:text-sm">
                Organisations and mentors can elevate motivation by offering innovative DOORI physical collectibles to participants who successfully complete a continuous 12-week streak journey.
            </p>

            <p className="text-gray-100 text-xs lg:text-sm mt-2">
                By sponsoring an additional reward contribution, mentors and institutions can enable Think5Whys to manufacture, package and dispatch exclusive collectible rewards to qualifying participants.
            </p>

            <p className="text-gray-100 text-xs lg:text-sm mt-2">
                These collectibles are designed to become meaningful symbols of discipline, consistency, growth and achievement rather than ordinary rewards.
            </p>
        </div>

        {/* BENEFITS */}
        <div className="bg-gray-800/70 border border-gray-700 rounded-xl p-4">
            <h2 className="text-sm lg:text-lg text-cyan-200 font-semibold mb-3">
                🌟 Why Parent & Mentor Streaks Work
            </h2>

            <ul className="list-disc pl-5 space-y-2">
                <li className="text-gray-100 text-xs lg:text-sm">
                    Encourages accountability and consistency.
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Makes progress visible and measurable.
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Builds stronger mentor-participant engagement.
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Reinforces positive habits through recognition.
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Increases completion rates and long-term outcomes.
                </li>

                <li className="text-gray-100 text-xs lg:text-sm">
                    Creates memorable achievement journeys through collectibles and rewards.
                </li>
            </ul>
        </div>

        {/* VISION */}
        <div className="bg-gradient-to-r from-purple-900/30 to-cyan-900/30 border border-purple-700 rounded-xl p-4">
            <h2 className="text-sm lg:text-lg text-cyan-200 font-semibold mb-3">
                🚀 PurpleDoor Vision
            </h2>

            <p className="text-gray-100 text-xs lg:text-sm">
                PurpleDoor Parent & Mentor Streaks transforms education, coaching, therapy, personal development and professional training into engaging growth journeys powered by consistency.
            </p>

            <p className="text-gray-100 text-xs lg:text-sm mt-2">
                By combining mentorship, accountability, rewards and innovative collectibles, PurpleDoor helps create stronger habits, better outcomes and lasting transformation for learners and participants of all ages.
            </p>
        </div>

    </div>
    );
};


export default PDParentStreak;